import { useEffect } from 'react';
import { RefreshCw, UploadCloud } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSchedule, useScheduleActions } from './state';
import type { PublishScheduleInput } from '@/lib/gql/__generated__/graphql';

const publishPayload: Pick<PublishScheduleInput, 'routes' | 'directions' | 'stops' | 'scheduledArrivals'> = {
  routes: [
    { routeId: 'e-line', routeName: 'E Line' },
    { routeId: 'route-44', routeName: 'Route 44' },
  ],
  directions: [
    { directionId: 'e-line-north', directionName: 'Downtown Seattle', routeId: 'e-line' },
    { directionId: 'route-44-ballard', directionName: 'Ballard', routeId: 'route-44' },
  ],
  stops: [
    { directionId: 'e-line-north', stopId: 'third-pike', stopName: '3rd Ave & Pike St' },
    { directionId: 'route-44-ballard', stopId: 'market-15th', stopName: 'Market St & 15th Ave NW' },
  ],
  scheduledArrivals: [
    {
      arrivalId: 'e-line-arrival-1',
      destinationLabel: 'Downtown Seattle',
      directionId: 'e-line-north',
      routeId: 'e-line',
      scheduledTime: '08:15',
      serviceDay: 'weekday',
      stopId: 'third-pike',
    },
    {
      arrivalId: 'route-44-arrival-1',
      destinationLabel: 'Ballard',
      directionId: 'route-44-ballard',
      routeId: 'route-44',
      scheduledTime: '08:30',
      serviceDay: 'weekday',
      stopId: 'market-15th',
    },
  ],
};

export function ScheduleCachePage() {
  const snapshot = useSchedule((value) => value);
  const actions = useScheduleActions();

  useEffect(() => {
    if (snapshot.matches('idle')) actions.loadBrowse();
  }, [actions, snapshot]);

  const isLoading = snapshot.matches('loading');
  const isPublishing = snapshot.matches('publishing');

  return (
    <AppShell
      title="Schedule cache"
      description="Keep the route board supplied with the latest published Seattle service patterns."
    >
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Operations</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {snapshot.context.items.length} published schedule{snapshot.context.items.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-jr-key="schedule-load"
            disabled={isLoading || isPublishing}
            onClick={actions.loadBrowse}
          >
            <RefreshCw className="size-4" aria-hidden="true" />
            Refresh cache
          </Button>
          <Button
            data-jr-key="publish-schedule"
            disabled={isLoading || isPublishing}
            onClick={() => actions.publish(publishPayload)}
          >
            <UploadCloud className="size-4" aria-hidden="true" />
            {isPublishing ? 'Publishing…' : 'Publish schedule'}
          </Button>
        </div>
      </div>

      {isLoading && <p className="py-12 text-center text-sm text-muted-foreground">Checking the schedule cache…</p>}
      {snapshot.matches('error') && (
        <div className="mt-6 rounded-[14px] border border-destructive/40 bg-destructive/5 p-5 text-sm text-destructive">
          We couldn’t load the schedule cache. Try refreshing to reconnect.
        </div>
      )}
      {snapshot.matches('ready') && snapshot.context.items.length === 0 && (
        <Card className="mt-6 border-dashed bg-card/60">
          <CardHeader>
            <CardTitle>No schedules published yet</CardTitle>
            <CardDescription>Publish the current E Line and Route 44 board to start the cache.</CardDescription>
          </CardHeader>
        </Card>
      )}
      {snapshot.matches('ready') && snapshot.context.items.length > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {snapshot.context.items.map((schedule) => (
            <Card key={schedule.scheduleId}>
              <CardContent className="flex items-center justify-between gap-4 py-5">
                <div>
                  <p className="font-medium text-foreground">Published service board</p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">{schedule.scheduleId}</p>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Ready</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
